import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import ContentCopyOutlined from "@mui/icons-material/ContentCopyOutlined";
import CheckOutlined from "@mui/icons-material/CheckOutlined";
import { useDispatchApp } from "../../../lib/redux";
import { useTranslationApp } from "../../../lib/i18next";
import { startCreateMcpApiKey } from "../../../actions/mcp";
import { SimpleAlert } from "../../../components/Alerts/Alerts";
import Button from "../../../components/Buttons/Button";
import { BOX_SHADOW_FIELDS, INPUT_COLOR_BORDER } from "../../../utils/colors";
import { BORDER_RADIUS } from "../../../utils/constants";
import {
  ERROR,
  MCP_AGENT_PROMPT,
  MCP_API_KEY_GENERATE_ERROR,
  MCP_API_KEY_PLACEHOLDER,
  MCP_API_KEY_SHOWN_ONCE_NOTICE,
  MCP_COPIED,
  MCP_GENERATE_API_KEY_BUTTON,
  MCP_HEADER_TITLE,
  MCP_SERVER_URL_NOT_AVAILABLE,
  MCP_STEP_AGENT_DESCRIPTION,
  MCP_STEP_AGENT_TITLE,
  MCP_STEP_API_KEY_DESCRIPTION,
  MCP_STEP_API_KEY_TITLE,
  MCP_STEP_URL_TITLE,
} from "../../../i18n/keysTranslations";

const BUSINESS_NAME = "Adle";
const COPY_FEEDBACK_MS = 2000;
const MCP_SERVER_URL = String(import.meta.env.VITE_MCP_SERVER_URL || "").trim();
const CARD_RADIUS = `${BORDER_RADIUS * 2}px`;
const MONO_FONT = "ui-monospace, SFMono-Regular, 'SF Mono', Menlo, monospace";

const CopyableValue = ({ value, placeholder, multiline = false, t }) => {
  const [isCopied, setIsCopied] = useState(false);
  const timeoutRef = useRef(null);

  useEffect(() => {
    setIsCopied(false);
  }, [value]);

  useEffect(() => () => window.clearTimeout(timeoutRef.current), []);

  const onCopy = async () => {
    if (!value || !navigator?.clipboard?.writeText) {
      return;
    }

    try {
      await navigator.clipboard.writeText(value);
      setIsCopied(true);
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = window.setTimeout(
        () => setIsCopied(false),
        COPY_FEEDBACK_MS
      );
    } catch {
      // ignore clipboard errors
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: multiline ? "flex-start" : "center",
        gap: 1,
        px: 1.5,
        py: multiline ? 1 : 0,
        minHeight: 44,
        borderRadius: `${BORDER_RADIUS}px`,
        border: `1px solid ${INPUT_COLOR_BORDER}`,
        backgroundColor: "background.paper",
        boxShadow: BOX_SHADOW_FIELDS,
      }}
    >
      <Typography
        sx={{
          flex: 1,
          minWidth: 0,
          alignSelf: "center",
          fontFamily: MONO_FONT,
          fontSize: 13,
          color: value ? "text.primary" : "text.secondary",
          ...(multiline
            ? { whiteSpace: "pre-wrap", wordBreak: "break-word" }
            : {
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }),
        }}
      >
        {value || placeholder}
      </Typography>
      {value ? (
        <IconButton
          type="button"
          size="small"
          onClick={onCopy}
          aria-label={t(MCP_COPIED)}
          sx={{ flexShrink: 0 }}
        >
          {isCopied ? (
            <CheckOutlined sx={{ fontSize: 18 }} />
          ) : (
            <ContentCopyOutlined sx={{ fontSize: 18 }} />
          )}
        </IconButton>
      ) : null}
    </Box>
  );
};

const Step = ({ index, title, description, isLast, children }) => (
  <Box sx={{ display: "flex", gap: 2 }}>
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        flexShrink: 0,
      }}
    >
      <Box
        sx={{
          width: 28,
          height: 28,
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 13,
          fontWeight: 700,
          color: "primary.contrastText",
          backgroundColor: "primary.main",
        }}
      >
        {index}
      </Box>
      {isLast ? null : (
        <Box
          sx={{
            flex: 1,
            width: "1px",
            my: 0.5,
            backgroundColor: INPUT_COLOR_BORDER,
          }}
        />
      )}
    </Box>
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        gap: 1,
        pb: isLast ? 0 : 3,
      }}
    >
      <Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          {title}
        </Typography>
        {description ? (
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            {description}
          </Typography>
        ) : null}
      </Box>
      {children}
    </Box>
  </Box>
);

const McpSettings = () => {
  const dispatch = useDispatchApp();
  const { t } = useTranslationApp();
  const [apiKey, setApiKey] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);

  const onGenerateApiKey = useCallback(async () => {
    setIsGenerating(true);
    const response = await dispatch(startCreateMcpApiKey());
    setIsGenerating(false);

    if (!response?.ok) {
      SimpleAlert({
        title: t(ERROR),
        text: t(MCP_API_KEY_GENERATE_ERROR),
        icon: "error",
      });
      return;
    }

    setApiKey(response.apiKey);
  }, [dispatch, t]);

  const agentPrompt = useMemo(
    () =>
      t(MCP_AGENT_PROMPT, {
        url: MCP_SERVER_URL,
        apiKey: apiKey || t(MCP_API_KEY_PLACEHOLDER),
        interpolation: { escapeValue: false },
      }),
    [apiKey, t]
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 3,
        maxWidth: 640,
        p: 3,
        borderRadius: CARD_RADIUS,
        border: `1px solid ${INPUT_COLOR_BORDER}`,
        backgroundColor: "background.paper",
      }}
    >
      <Box>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          {t(MCP_HEADER_TITLE, { name_business: BUSINESS_NAME })}
        </Typography>
      </Box>

      <Box>
        <Step index={1} title={t(MCP_STEP_URL_TITLE)}>
          <CopyableValue
            value={MCP_SERVER_URL}
            placeholder={t(MCP_SERVER_URL_NOT_AVAILABLE)}
            t={t}
          />
        </Step>

        <Step
          index={2}
          title={t(MCP_STEP_API_KEY_TITLE)}
          description={t(MCP_STEP_API_KEY_DESCRIPTION)}
        >
          {apiKey ? (
            <>
              <CopyableValue value={apiKey} t={t} />
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                {t(MCP_API_KEY_SHOWN_ONCE_NOTICE)}
              </Typography>
            </>
          ) : null}
          <Box>
            <Button
              type="button"
              color={apiKey ? "secondary" : "primary"}
              loading={isGenerating}
              disabled={isGenerating}
              onClick={onGenerateApiKey}
            >
              {t(MCP_GENERATE_API_KEY_BUTTON)}
            </Button>
          </Box>
        </Step>

        <Step
          index={3}
          isLast
          title={t(MCP_STEP_AGENT_TITLE)}
          description={t(MCP_STEP_AGENT_DESCRIPTION)}
        >
          <CopyableValue value={agentPrompt} multiline t={t} />
        </Step>
      </Box>
    </Box>
  );
};

export default McpSettings;
